import React, { useState, useRef, useEffect } from 'react';
import { BuildingOffice2Icon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';

const OrganizationSelector = ({ className = '' }) => {
  const {
    organizations,
    selectedOrgId,
    setSelectedOrgId,
    loading,
    hasOrganizations,
    orgFarmCounts,
    isAdmin,
  } = useFarm();

  // For non-admin members the "all" option means "my own farms" — labelling it
  // "All Organizations" is misleading because they aren't actually seeing every
  // org on the platform, just the ones they're connected to. Switch the labels
  // accordingly so members understand what each option does.
  const defaultOptionLabel = isAdmin ? 'All Organizations' : 'My Farms';

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle organization selection
  const handleSelect = (orgId) => {
    const parsedOrgId = orgId === 'all' ? 'all' : parseInt(orgId);
    setSelectedOrgId(parsedOrgId);
    setIsOpen(false);
    setSearchQuery('');
    console.log('🏢 Organization selected:', parsedOrgId === 'all' ? 'All Organizations' : organizations.find(o => o.id === parsedOrgId)?.name_org);
  };

  // Get farm count for an organization from both sources
  const getFarmCount = (orgId) => {
    // Keys in orgFarmCounts are strings, so convert orgId to string
    return orgFarmCounts[String(orgId)] || 0;
  };

  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org => 
    org.name_org?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected organization label
  const getSelectedOrgLabel = () => {
    if (selectedOrgId === 'all') {
      return isAdmin
        ? `All Organizations (${organizations.length})`
        : defaultOptionLabel;
    }
    const selectedOrg = organizations.find(o => o.id === selectedOrgId);
    return selectedOrg ? selectedOrg.name_org : 'Select Organization';
  };

  if (loading) {
    return (
      <div className={`inline-block ${className}`}>
        <button className="input w-64 text-left cursor-wait" disabled>
          Loading organizations...
        </button>
      </div>
    );
  }

  if (!hasOrganizations) {
    return null;
  }

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="input pl-10 pr-10 w-64 text-left cursor-pointer flex items-center justify-between"
      >
        <BuildingOffice2Icon className="h-5 w-5 absolute left-3 text-gray-400" />
        <span className="truncate">{getSelectedOrgLabel()}</span>
        <ChevronDownIcon className={`h-5 w-5 absolute right-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search organizations..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-80 overflow-y-auto">
            {/* Default option — for admin this is "All Organizations", for a
                member it is "My Farms" (their own assigned farms). */}
            <div
              onClick={() => handleSelect('all')}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                selectedOrgId === 'all' ? 'bg-primary-50 text-primary-600' : ''
              }`}
            >
              <BuildingOffice2Icon className="h-5 w-5 text-gray-400" />
              <span className="font-medium">
                {isAdmin
                  ? `All Organizations (${organizations.length})`
                  : defaultOptionLabel}
              </span>
            </div>

            {/* Filtered Organizations */}
            {filteredOrganizations.length > 0 ? (
              filteredOrganizations.map((org) => {
                const farmCount = getFarmCount(org.id);
                return (
                  <div
                    key={org.id}
                    onClick={() => handleSelect(org.id)}
                    className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                      selectedOrgId === org.id ? 'bg-primary-50 text-primary-600' : ''
                    }`}
                  >
                    <BuildingOffice2Icon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{org.name_org}</div>
                      <div className="text-xs text-gray-500">
                        {farmCount} farm{farmCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No organizations found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSelector;
